import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useEffect, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { handleDomBubbleEvent } from '@/utils/dom';
import {
  ResendInviteAsAdminTicketingUserValidationSchema,
  TResendInviteAsAdminTicketingUserValidation,
  useResendInviteAsAdminTicketingUserMutation,
} from '../../api/resend-invite-as-admin-ticketing-user.api';
import { GET_LIST_TICKETING_USER_INVITATION_QUERY_KEY } from '../../api/get-list-invitation-ticketing-user.api';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';

import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';

const TOAST_MESSAGES_RESEND_INVITATION_TICKETING_USER = {
  RESEND_INVITATION_TICKETING_USER_ERROR: 'Error resending invitation, please try again',
  RESEND_INVITATION_TICKETING_USER_SUCCESS: 'The invitation has been successfully resent',
};

export type TResendInvitationTicketingUserModalType = 'ResendInvitationTicketingUserModal';
export interface IResendInvitationTicketingUserModalProps {
  invitationId: string;
}
const ResendInvitationTicketingUserModal = memo((props: IResendInvitationTicketingUserModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();
  const form = useForm<TResendInviteAsAdminTicketingUserValidation>({
    resolver: zodResolver(ResendInviteAsAdminTicketingUserValidationSchema),
    mode: 'onChange',
  });

  const mutationResendInvitationTicketingUser = useResendInviteAsAdminTicketingUserMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage =
          axiosExtractErrorMessage(error) ||
          TOAST_MESSAGES_RESEND_INVITATION_TICKETING_USER.RESEND_INVITATION_TICKETING_USER_ERROR;
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_LIST_TICKETING_USER_INVITATION_QUERY_KEY] });
        modalActions.closeModal('ResendInvitationTicketingUserModal');
        showToast({
          message: TOAST_MESSAGES_RESEND_INVITATION_TICKETING_USER.RESEND_INVITATION_TICKETING_USER_SUCCESS,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenResendInvitationTicketingUserModal = useMemo(() => {
    return modalActions.checkIsOpenModal('ResendInvitationTicketingUserModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationResendInvitationTicketingUser.isPending;
  }, [mutationResendInvitationTicketingUser.isPending]);

  const isLoadingForm = useMemo(() => {
    return mutationResendInvitationTicketingUser.isPending;
  }, [mutationResendInvitationTicketingUser.isPending]);

  // EFFECTS
  useEffect(() => {
    if (props?.invitationId) {
      form.reset({
        invitationId: props.invitationId,
      });
    }
  }, [props?.invitationId, form]);

  // HANDLERS
  const handleSubmitForm = useCallback(
    (data: TResendInviteAsAdminTicketingUserValidation) => {
      mutationResendInvitationTicketingUser.mutate({
        invitationId: data?.invitationId || '',
      });
    },
    [mutationResendInvitationTicketingUser]
  );

  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('ResendInvitationTicketingUserModal');
  }, [modalActions]);

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  return (
    <Modal
      isOpen={isOpenResendInvitationTicketingUserModal}
      lockBodyScroll
      onClose={handleClose}
      className="p-[32px] md:w-[500px] w-[345px]"
    >
      <ModalHeader>
        <ModalTitle>Resend Invitation</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitForm)(e);
          }}
          id="resend-invitation-ticketing-user-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to resend this invitation?</span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="resend-invitation-ticketing-user-form" type="submit" disabled={isDisabledForm}>
          {isLoadingForm ? <Spinner className="w-[18px] h-[18px]" /> : null} Resend
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
ResendInvitationTicketingUserModal.displayName = 'ResendInvitationTicketingUserModal';

export default ResendInvitationTicketingUserModal;
